import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import { TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import { stringArrayQueryParam, optionalStringQueryParam, useQueryStringParams, numberQueryParam } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useEffect, useMemo } from "react";
import { DimensionTypes } from "~/areas/projects/components/DashboardDataSource/DataCube";
import useLocalStorage from "~/hooks/useLocalStorage";
export interface ProjectDashboardPageFilters {
    [key: number]: {
        [key: string]: boolean;
    };
    groupBy?: DimensionTypes;
    groupByExtra?: string;
    rowDimension?: DimensionTypes;
    columnDimension?: DimensionTypes;
    page: number;
    pageSize: ProjectDashboardPageSize;
}
export const projectDashboardPageSizes = [50, 100, 150] as const;
export type ProjectDashboardPageSize = (typeof projectDashboardPageSizes)[number];
export const defaultProjectDashboardPageSize: ProjectDashboardPageSize = 50;
export function isProjectDashboardPageSize(pageSize: number | undefined): pageSize is ProjectDashboardPageSize {
    if (pageSize === undefined)
        return false;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (projectDashboardPageSizes as readonly number[]).includes(pageSize);
}
interface ProjectDashboardPageQueryParams {
    groupBy: string | undefined;
    tags: string[];
    tenants: string[];
    release: string | undefined;
    groupByExtra: string | undefined;
    environments: string[];
}
const tagsParameter = stringArrayQueryParam("tags");
const tenantsParameter = stringArrayQueryParam("tenants");
const releaseParameter = optionalStringQueryParam("release");
const groupByParameter = optionalStringQueryParam("groupBy");
const groupByExtraParameter = optionalStringQueryParam("groupByExtra");
const environmentsParameter = stringArrayQueryParam("environments");
const pageParameter = numberQueryParam("page");
const pageSizeParameter = numberQueryParam("pageSize");
export default function useProjectDashboardPageFilters(project: ProjectResource): [
    ProjectDashboardPageFilters,
    (newFilters: ProjectDashboardPageFilters) => void
] {
    const [filters, setFilters] = useQueryStringParams([tagsParameter, tenantsParameter, releaseParameter, groupByParameter, groupByExtraParameter, environmentsParameter, pageParameter, pageSizeParameter]);
    const [userDefaultProjectDashboardPageSize, setUserDefaultProjectDashboardPageSize] = useLocalStorage(`Octopus.ProjectDashboardPage.PageSize`, defaultProjectDashboardPageSize);
    const isProjectTenanted = project.TenantedDeploymentMode !== TenantedDeploymentMode.Untenanted;
    const projectDashboardPageFiltersState: ProjectDashboardPageFilters = useMemo((): ProjectDashboardPageFilters => {
        const filtersForProject: ProjectDashboardPageFilters = {
            [DimensionTypes.Project]: { [project.Id]: true },
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            groupBy: filters.groupBy ? DimensionTypes[filters.groupBy as keyof typeof DimensionTypes] : undefined,
            groupByExtra: filters.groupByExtra,
            columnDimension: DimensionTypes.Environment,
            rowDimension: isProjectTenanted ? DimensionTypes.Tenant : DimensionTypes.Release,
            page: filters.page ?? 1,
            pageSize: isProjectDashboardPageSize(filters.pageSize) ? filters.pageSize : userDefaultProjectDashboardPageSize,
        };
        if (filters.release) {
            filtersForProject[DimensionTypes.Release] = { [filters.release]: true };
        }
        if (filters.tags.length > 0) {
            filtersForProject[DimensionTypes.TagSet] = filters.tags.reduce((acc: {
                [key: string]: boolean;
            }, cur) => {
                acc[cur] = true;
                return acc;
            }, {});
        }
        if (filters.tenants.length > 0) {
            filtersForProject[DimensionTypes.Tenant] = filters.tenants.reduce((acc: {
                [key: string]: boolean;
            }, cur) => {
                acc[cur] = true;
                return acc;
            }, {});
        }
        if (filters.environments.length > 0) {
            filtersForProject[DimensionTypes.Environment] = filters.environments.reduce((acc: {
                [key: string]: boolean;
            }, cur) => {
                acc[cur] = true;
                return acc;
            }, {});
        }
        return filtersForProject;
    }, [filters.environments, filters.groupBy, filters.groupByExtra, filters.page, filters.pageSize, filters.release, filters.tags, filters.tenants, isProjectTenanted, project.Id, userDefaultProjectDashboardPageSize]);
    if (userDefaultProjectDashboardPageSize !== projectDashboardPageFiltersState.pageSize) {
        setUserDefaultProjectDashboardPageSize(projectDashboardPageFiltersState.pageSize);
    }
    const setProjectDashboardPageFiltersState = (newFilter: ProjectDashboardPageFilters) => {
        setFilters({
            tags: newFilter[DimensionTypes.TagSet] ? Object.keys(newFilter[DimensionTypes.TagSet]) : undefined,
            tenants: newFilter[DimensionTypes.Tenant] ? Object.keys(newFilter[DimensionTypes.Tenant]) : undefined,
            release: newFilter[DimensionTypes.Release] ? Object.keys(newFilter[DimensionTypes.Release])[0] : undefined,
            groupBy: newFilter.groupBy !== undefined ? DimensionTypes[newFilter.groupBy] : undefined,
            groupByExtra: newFilter.groupByExtra,
            environments: newFilter[DimensionTypes.Environment] ? Object.keys(newFilter[DimensionTypes.Environment]) : undefined,
            page: newFilter.page,
            pageSize: newFilter.pageSize,
        });
    };
    const filtersWithDefault: ProjectDashboardPageQueryParams = useMemo(() => {
        return {
            ...filters,
            groupBy: filters.groupBy === undefined ? (isProjectTenanted ? DimensionTypes[DimensionTypes.None] : DimensionTypes[DimensionTypes.Channel]) : filters.groupBy,
        };
    }, [filters, isProjectTenanted]);
    useEffect(() => {
        if (!isEqual(filtersWithDefault, filters))
            setFilters(filtersWithDefault);
    }, [filtersWithDefault, filters, setFilters]);
    return [projectDashboardPageFiltersState, setProjectDashboardPageFiltersState];
}
